import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as ReactBootstrap from 'react-bootstrap';
import { Tooltip } from 'react-tooltip';
import useToolbarSetter from '../../hooks/Common/useToolbarSetter';
import LoadingPage from '../../layout/LoadingPage';
import { setCachedLocationList } from '../../redux/cachedLocationsReducer';
import CommonPagination from '../Common/CommonPagination';
import CommonTable from '../Common/CommonTable/CommonTable';
import { CommonModal } from '../Common/CommonModal';
import getLocations from '../../web_api/methods/settings/locations/getLocations';
import getAzureFile from '../../web_api/methods/files/getAzureFile';
import addTechnicalIncident from '../../web_api/methods/technicalIncidents/addTechnicalIncident';
import addTechnicalIncidentLog from '../../web_api/methods/technicalIncidents/addTechnicalIncidentLog';
import getTechnicalIncidentEventTypes from '../../web_api/methods/technicalIncidents/getTechnicalIncidentEventTypes';
import getTechnicalIncidents from '../../web_api/methods/technicalIncidents/getTechnicalIncidents';
import getTechnicalIncidentStatuses from '../../web_api/methods/technicalIncidents/getTechnicalIncidentStatuses';
import updateTechnicalIncidentStatus from '../../web_api/methods/technicalIncidents/updateTechnicalIncidentStatus';
import { locationsList, technicalIncidentEventTypeList, technicalIncidentList, technicalIncidentStatusList } from '../../web_api/models';
import TechnicalIncidentAddModal from './TechnicalIncidentAddModal';

const INCIDENTS_PAGE_SIZE = 30;

const formatTimestamp = (timestamp: string | null | undefined) => {
  if (!timestamp) {
    return '-';
  }

  const parsedDate = new Date(timestamp);

  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleString();
};

const sortIncidents = (incidentList: technicalIncidentList) => {
  return [...incidentList].sort((firstIncident, secondIncident) => {
    const firstTimestamp = firstIncident.created_at ? new Date(firstIncident.created_at).getTime() : 0;
    const secondTimestamp = secondIncident.created_at ? new Date(secondIncident.created_at).getTime() : 0;

    return secondTimestamp - firstTimestamp;
  });
};

const dropdownMenuStyle: React.CSSProperties & Record<string, string | number> = {
  zIndex: 1080,
  '--bs-dropdown-link-active-bg': 'transparent',
  '--bs-dropdown-link-active-color': 'inherit',
};

type TechnicalIncidentWithImageUrl = technicalIncidentList[number] & {
  imageUrl: string | null;
};

const TechnicalIncidents = () => {
  const dispatch = useDispatch();
  const { setToolbar } = useToolbarSetter();

  const [incidents, setIncidents] = useState<TechnicalIncidentWithImageUrl[]>([]);
  const [locations, setLocations] = useState<locationsList>([]);
  const [eventTypes, setEventTypes] = useState<technicalIncidentEventTypeList>([]);
  const [statuses, setStatuses] = useState<technicalIncidentStatusList>([]);
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [loadingIncidents, setLoadingIncidents] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<TechnicalIncidentWithImageUrl | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [updatingIncidentId, setUpdatingIncidentId] = useState<number | null>(null);
  const [submittingLogIncidentId, setSubmittingLogIncidentId] = useState<number | null>(null);
  const [expandedIncidentId, setExpandedIncidentId] = useState<number | null>(null);
  const [incidentLogDrafts, setIncidentLogDrafts] = useState<Record<number, string>>({});
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [filters, setFilters] = useState({
    statusId: 0,
    eventTypeId: 0,
  });
  const [reloadKey, setReloadKey] = useState(0);

  const getNextStatus = (statusId: number | null) => {
    if (!statusId) {
      return undefined;
    }

    const currentStatusIndex = statuses.findIndex((status) => status.id === statusId);

    if (currentStatusIndex === -1) {
      return undefined;
    }

    return statuses[currentStatusIndex + 1];
  };

  const refreshIncidents = () => {
    setReloadKey((currentReloadKey) => currentReloadKey + 1);
  };

  const handleReloadAfterMutation = () => {
    setExpandedIncidentId(null);

    if (pageIndex !== 0) {
      setPageIndex(0);
      return;
    }

    refreshIncidents();
  };

  const handleToggleImageModal = (incident?: TechnicalIncidentWithImageUrl | null) => {
    if (incident?.imageUrl) {
      setSelectedIncident(incident);
      setShowImageModal(true);
      return;
    }

    setShowImageModal(false);
    setSelectedIncident(null);
  };

  const handleFilterChange = (filterName: 'statusId' | 'eventTypeId', value: number) => {
    setExpandedIncidentId(null);
    setPageIndex(0);
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  const handlePageChange = (direction: 'next' | 'previous') => {
    if (loadingIncidents) {
      return;
    }

    setExpandedIncidentId(null);
    setPageIndex((currentPageIndex) => {
      if (direction === 'next') {
        return currentPageIndex + 1;
      }

      return Math.max(currentPageIndex - 1, 0);
    });
  };

  const handleAddIncident = async ({ cameraId, eventTypeId, image, message }: { cameraId: number; eventTypeId: number; image: File; message?: string }) => {
    try {
      setSubmitting(true);
      await addTechnicalIncident({ cameraId, eventTypeId, image, message });
      setShowAddModal(false);
      handleReloadAfterMutation();
      toast.success('Technical incident created successfully!', {
        position: 'top-right',
        theme: 'colored',
        autoClose: 2000,
      });
    } catch (error: any) {
      console.log(error);
      toast.error(`${error}`, {
        position: 'top-right',
        theme: 'colored',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateIncidentStatus = async (incidentId: number, nextStatusId: number, nextStatusName: string) => {
    try {
      setUpdatingIncidentId(incidentId);
      await updateTechnicalIncidentStatus({ id: incidentId, status_id: nextStatusId });
      handleReloadAfterMutation();
      toast.success(`Technical incident marked as ${nextStatusName}.`, {
        position: 'top-right',
        theme: 'colored',
        autoClose: 2000,
      });
    } catch (error: any) {
      console.log(error);
      toast.error(`${error}`, {
        position: 'top-right',
        theme: 'colored',
      });
    } finally {
      setUpdatingIncidentId(null);
    }
  };

  const handleIncidentLogDraftChange = (incidentId: number, value: string) => {
    setIncidentLogDrafts((currentDrafts) => ({
      ...currentDrafts,
      [incidentId]: value,
    }));
  };

  const handleAddIncidentLog = async (incidentId: number) => {
    const message = incidentLogDrafts[incidentId]?.trim();

    if (!message) {
      return;
    }

    try {
      setSubmittingLogIncidentId(incidentId);
      await addTechnicalIncidentLog({ incident_id: incidentId, message });
      setIncidentLogDrafts((currentDrafts) => ({
        ...currentDrafts,
        [incidentId]: '',
      }));
      setExpandedIncidentId(incidentId);
      refreshIncidents();
      toast.success('Log added successfully!', {
        position: 'top-right',
        theme: 'colored',
        autoClose: 2000,
      });
    } catch (error: any) {
      console.log(error);
      toast.error(`${error}`, {
        position: 'top-right',
        theme: 'colored',
      });
    } finally {
      setSubmittingLogIncidentId(null);
    }
  };

  useEffect(() => {
    setToolbar('technical-incidents', 'Technical Incidents');
  }, []);

  useEffect(() => {
    const loadMetadata = async () => {
      try {
        setLoadingMetadata(true);

        const [loadedLocations, loadedEventTypes, loadedStatuses] = await Promise.all([
          getLocations(),
          getTechnicalIncidentEventTypes(),
          getTechnicalIncidentStatuses(),
        ]);

        setLocations(loadedLocations);
        setEventTypes(loadedEventTypes);
        setStatuses(loadedStatuses);
        dispatch(setCachedLocationList({ list: loadedLocations }));
      } catch (error: any) {
        console.log(error);
        toast.error(`${error}`, {
          position: 'top-right',
          theme: 'colored',
        });
      } finally {
        setLoadingMetadata(false);
      }
    };

    loadMetadata();
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadIncidents = async () => {
      try {
        setLoadingIncidents(true);

        const loadedIncidents = await getTechnicalIncidents({
          status: filters.statusId || undefined,
          eventType: filters.eventTypeId || undefined,
          skip: pageIndex * INCIDENTS_PAGE_SIZE,
        });

        if (ignore) {
          return;
        }

        if (loadedIncidents.length === 0 && pageIndex > 0) {
          setPageIndex((currentPageIndex) => Math.max(currentPageIndex - 1, 0));
          return;
        }

        setHasNextPage(loadedIncidents[0]?.has_next_page === true);

        const incidentsWithImageUrls = await Promise.all(
          sortIncidents(loadedIncidents).map(async (incident) => ({
            ...incident,
            imageUrl: incident.filename ? await getAzureFile(incident.filename) : null,
          }))
        );

        if (ignore) {
          return;
        }

        setIncidents(incidentsWithImageUrls);
        setExpandedIncidentId((currentExpandedIncidentId) =>
          currentExpandedIncidentId !== null && incidentsWithImageUrls.some((incident) => incident.id === currentExpandedIncidentId)
            ? currentExpandedIncidentId
            : null
        );
        setSelectedIncident((currentSelectedIncident) =>
          currentSelectedIncident
            ? incidentsWithImageUrls.find((incident) => incident.id === currentSelectedIncident.id) ?? currentSelectedIncident
            : null
        );
      } catch (error: any) {
        if (ignore) {
          return;
        }

        console.log(error);
        toast.error(`${error}`, {
          position: 'top-right',
          theme: 'colored',
        });
      } finally {
        if (!ignore) {
          setLoadingIncidents(false);
        }
      }
    };

    loadIncidents();

    return () => {
      ignore = true;
    };
  }, [filters.eventTypeId, filters.statusId, pageIndex, reloadKey]);

  if (loadingMetadata || (loadingIncidents && incidents.length === 0 && pageIndex === 0)) {
    return <LoadingPage />;
  }

  return (
    <>
      <div className='container py-3 py-lg-6 px-8 mb-12 mb-md-0'>
        <div className='card mb-5'>
          <div className='card-body py-5'>
            <div className='row g-5 align-items-end'>
              <div className='col-xl-4 col-md-6'>
                <ReactBootstrap.Form.Group controlId='technical-incident-filter-status'>
                  <ReactBootstrap.Form.Label>Status</ReactBootstrap.Form.Label>
                  <ReactBootstrap.Form.Select
                    disabled={loadingIncidents}
                    value={filters.statusId || ''}
                    onChange={(event) => handleFilterChange('statusId', Number(event.target.value))}
                  >
                    <option value=''>All statuses</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </ReactBootstrap.Form.Select>
                </ReactBootstrap.Form.Group>
              </div>
              <div className='col-xl-4 col-md-6'>
                <ReactBootstrap.Form.Group controlId='technical-incident-filter-event-type'>
                  <ReactBootstrap.Form.Label>Event Type</ReactBootstrap.Form.Label>
                  <ReactBootstrap.Form.Select
                    disabled={loadingIncidents}
                    value={filters.eventTypeId || ''}
                    onChange={(event) => handleFilterChange('eventTypeId', Number(event.target.value))}
                  >
                    <option value=''>All event types</option>
                    {eventTypes.map((eventType) => (
                      <option key={eventType.id} value={eventType.id}>
                        {eventType.name}
                      </option>
                    ))}
                  </ReactBootstrap.Form.Select>
                </ReactBootstrap.Form.Group>
              </div>
              <div className='col-xl-4 col-md-12 d-flex justify-content-xl-end'>
                <CommonPagination
                  disablePrevious={pageIndex === 0 || loadingIncidents}
                  disableNext={loadingIncidents || hasNextPage !== true}
                  startIndex={pageIndex + 1}
                  handlePrevChange={() => handlePageChange('previous')}
                  handleNextChange={() => handlePageChange('next')}
                  containerClass='p-0'
                />
              </div>
            </div>
          </div>
        </div>
        <CommonTable
          title='Technical Incidents'
          subtitle={`${incidents.length} incidents`}
          addButtonText='NEW +'
          hasCheckedRows={0}
          onClickAdd={() => setShowAddModal(true)}
          onClickDelete={() => undefined}
          tableClassName='table-row-dashed'
          columns={
            <>
              <th className='w-40px' />
              <th>Camera Location</th>
              <th>Camera Name</th>
              <th>Status Name</th>
              <th>Event Type Name</th>
              <th>Logged by</th>
              <th>Created At</th>
              <th>In Progress At</th>
              <th>Marked As Done At</th>
              <th>Resolved At</th>
              <th>Actions</th>
            </>
          }
          rows={
            incidents.length > 0 ? (
              <>
                {incidents.map((incident) => {
                  const nextStatus = getNextStatus(incident.status_id);
                  const isExpanded = expandedIncidentId === incident.id;

                  return (
                    <Fragment key={incident.id}>
                      <tr className='cursor-pointer' onClick={() => setExpandedIncidentId(isExpanded ? null : incident.id)}>
                        <td className='text-center'>
                          <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} text-primary`} />
                        </td>
                        <td className='fw-semibold fs-7'>{incident.camera_location_id || '-'}</td>
                        <td className='fw-semibold fs-7'>{incident.camera_name || '-'}</td>
                        <td className='fw-semibold fs-7'>{incident.status_name || '-'}</td>
                        <td className='fw-semibold fs-7'>{incident.event_type_name || '-'}</td>
                        <td className='fw-semibold fs-7'>{incident.logged_by_user_name || '-'}</td>
                        <td className='fw-semibold fs-7'>{formatTimestamp(incident.created_at)}</td>
                        <td className='fw-semibold fs-7'>{formatTimestamp(incident.in_progress_at)}</td>
                        <td className='fw-semibold fs-7'>{formatTimestamp(incident.marked_as_done_at)}</td>
                        <td className='fw-semibold fs-7'>{formatTimestamp(incident.resolved_at)}</td>
                        <td className='fw-semibold fs-7 text-end d-flex flex-row align-items-center' onClick={(event) => event.stopPropagation()}>
                          {incident.imageUrl ? (
                            <>
                              <a
                                className='text-primary bg-light-primary p-3 rounded cursor-pointer me-2'
                                id={`incident-image-${incident.id}`}
                                onClick={() => handleToggleImageModal(incident)}
                              >
                                <i className='bi bi-camera p-0 text-primary' />
                              </a>
                              <Tooltip anchorSelect={`#incident-image-${incident.id}`}>See image</Tooltip>
                            </>
                          ) : (
                            <span className='text-muted me-2'>No image</span>
                          )}
                          <ReactBootstrap.Dropdown align='end' as='span'>
                            <ReactBootstrap.Dropdown.Toggle
                              bsPrefix='btn'
                              variant='light'
                              className='text-primary bg-light-primary p-3 rounded cursor-pointer border-0 shadow-none'
                              id={`incident-actions-${incident.id}`}
                            >
                              <i className='bi bi-three-dots p-0 text-primary' />
                            </ReactBootstrap.Dropdown.Toggle>
                            <ReactBootstrap.Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }} style={dropdownMenuStyle}>
                              {nextStatus ? (
                                <ReactBootstrap.Dropdown.Item
                                  disabled={updatingIncidentId === incident.id}
                                  onClick={() => handleUpdateIncidentStatus(incident.id, nextStatus.id, nextStatus.name)}
                                >
                                  {updatingIncidentId === incident.id ? (
                                    <ReactBootstrap.Spinner animation='border' className='me-2' size='sm' />
                                  ) : null}
                                  {`Mark as ${nextStatus.name}`}
                                </ReactBootstrap.Dropdown.Item>
                              ) : (
                                <ReactBootstrap.Dropdown.Item disabled>No next status available</ReactBootstrap.Dropdown.Item>
                              )}
                            </ReactBootstrap.Dropdown.Menu>
                          </ReactBootstrap.Dropdown>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr>
                          <td colSpan={12} className='bg-light-primary'>
                            <div className='px-4'>
                              {incident.logs.length > 0 ? (
                                <div className='d-flex flex-column gap-2'>
                                  {incident.logs.map((log, logIndex) => (
                                    <div key={`${incident.id}-${log.id}-${log.created_at || logIndex}`} className='rounded p-4 bg-white'>
                                      <div className='d-flex flex-column flex-md-row justify-content-between gap-2 mb-1'>
                                        <span className='fw-bold fs-7'>{log.sent_by_user_name || '-'}</span>
                                        <span className='text-muted fs-8'>{formatTimestamp(log.created_at)}</span>
                                      </div>
                                      <div className='text-gray-800 fs-7' style={{ whiteSpace: 'pre-wrap' }}>
                                        {log.message || '-'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className='text-muted fw-semibold fs-7'>No logs available.</div>
                              )}
                              <div className='border-top border-gray-300 mt-5 pt-5'>
                                <div className='d-flex flex-column flex-md-row gap-3 align-items-stretch'>
                                  <ReactBootstrap.Form.Control
                                    as='textarea'
                                    rows={1}
                                    className='resize-none'
                                    placeholder='Write a new log message...'
                                    value={incidentLogDrafts[incident.id] ?? ''}
                                    disabled={submittingLogIncidentId === incident.id}
                                    onChange={(event) => handleIncidentLogDraftChange(incident.id, event.target.value)}
                                  />
                                  <ReactBootstrap.Button
                                    variant='primary'
                                    className='d-flex align-items-center justify-content-center gap-2 px-6'
                                    disabled={submittingLogIncidentId === incident.id || !(incidentLogDrafts[incident.id]?.trim())}
                                    onClick={() => handleAddIncidentLog(incident.id)}
                                  >
                                    {submittingLogIncidentId === incident.id ? (
                                      <ReactBootstrap.Spinner animation='border' size='sm' />
                                    ) : (
                                      <i className='bi bi-send' />
                                    )}
                                    Send
                                  </ReactBootstrap.Button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </>
            ) : (
              <tr>
                <td className='text-muted fw-semibold fs-7 text-center py-10' colSpan={10}>
                  No technical incidents found.
                </td>
              </tr>
            )
          }
        />
      </div>
      <TechnicalIncidentAddModal
        eventTypes={eventTypes}
        locations={locations}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleAddIncident}
        show={showAddModal}
        submitting={submitting}
      />
      <CommonModal
        size='xl'
        title={
          selectedIncident ? `${selectedIncident.camera_name || 'Unknown camera'} - ${selectedIncident.event_type_name || 'Unknown event type'}` : 'Technical Incident Image'
        }
        show={showImageModal}
        showModalCallback={() => handleToggleImageModal()}
        closeButton={true}
        confirmButton={false}
        bodyClass='flex-column w-100'
        closeButtonOptions={{
          icon: 'bi bi-x-lg',
          variant: 'secondary',
          class: '',
        }}
        confirmButtonOptions={{
          icon: 'bi bi-check-lg',
          variant: 'custom',
          class: '',
        }}
      >
        {selectedIncident?.imageUrl ? (
          <img className='w-100' src={selectedIncident.imageUrl} alt={`${selectedIncident.camera_name || 'Technical incident'} incident`} />
        ) : (
          <div className='text-muted fw-semibold fs-7'>No image available for this incident.</div>
        )}
      </CommonModal>
    </>
  );
};

export default TechnicalIncidents;
